import { formatQueryForJsonApi } from '@evolve/common-ui';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  JsonApiResponseDto,
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import { TEmployeeStatus } from '../..';

export enum DigitalSignatureStatus {
  CREATING = 'creating',
  DRAFT = 'draft',
  SENDING = 'sending',
  IN_PROGRESS = 'inprogress',
  CONCLUDED = 'concluded',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  ERROR = 'error',
  NULLED = 'nulled',
}

export enum DigitalSignatureSubscriberStatus {
  TO_START = 'tostart',
  WAITING = 'waiting',
  CONCLUDED = 'concluded',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  MANUAL = 'manual',
}

export enum DigitalSignatureCategory {
  CTT = 'CTT',
  CUTT = 'CUTT',
  CT = 'CT',
  CPS = 'CPS',
  FO = 'FO',
}

export enum DigitalSignatureSubscriberType {
  GUEST = 'guest',
  REGISTERED = 'registered',
  GUEST_BIOMETRIC = 'guest_biometric',
  MANUAL = 'manual',
}

export enum DigitalSignatureProcessSignatureType {
  SIMPLE = 'simple',
  BIOMETRIC = 'biometric',
  MANUAL = 'manual',
}

export type TDigitalSignatureDocument = {
  id: string;
  docId: string;
  name: string;
  description: string;
  signedDoc?: TDigitalSignatureDocument;
  signed?: boolean;
  fixed: boolean;
};

export type TSignatureSubscriber = {
  id?: string;
  category?: DigitalSignatureSubscriberType;
  name?: string;
  email?: string;
  usersID?: string;
  companyID?: string;
  phone?: string;
  status?: DigitalSignatureSubscriberStatus;
  notify?: boolean;
  shouldRead?: boolean;
  shouldReceiveSignedDocs?: boolean;
  fixed?: boolean;
};

export type TSingleRegisteredUser = {
  id: string;
  username: string;
  userEmail: string;
  usersID: string;
  companyID: string;
  signatureType: string;
};

export type TRegisteredUsers = {
  registeredUsers: TSingleRegisteredUser[];
};

export type TDigitalSignatureCreator = {
  id?: number;
  name: string;
  acronym: string;
  email: string;
};

export type TCustomer = {
  id: string;
  name: string;
  number: string;
  email: string;
  customerStatusId: number;
  caCodeId: number;
};

export type TAgency = {
  id: string;
  name: string;
  acronym: string;
  active: number;
  address: string;
  phone: string;
  location: string;
  postalCode: string;
};

export type TEmployee = {
  id: number;
  code: string;
  name: string;
  email: string;
  birthDate: string;
  phone: string;
  socialSecurityNumber: number;
  applicantEmployeeStatusId: number;
  regimeId: number;
};

export type TCostCenter = {
  id: number;
  agencyId: number;
  customerId: number;
  description: string;
  reference: string;
  responsibleId?: number;
};

export type TRequestEmployee = {
  id: string;
  reference: string;
  proposalId: string;
  proposalReference: string;
};

export type TSelectedEmployee = {
  id: string;
  name: string;
  requestEmployeeCategoryId: string;
  requestEmployee: TRequestEmployee;
};

export type TEmployeeContract = {
  id: string;
  number?: string;
  startDate?: string;
  customer?: TCustomer;
  costCenter?: TCostCenter;
  employee?: TEmployee;
  agency?: TAgency;
  selectedEmployee?: TSelectedEmployee;
};

export type TCustomerContracts = {
  id: string;
  cuttId?: string;
  contractNumber?: string;
  reference?: string;
  startDate?: string;
  customer?: TCustomer;
  costCenter?: TCostCenter;
  agency?: TAgency;
  selectedEmployee?: TSelectedEmployee;
  contractStatusId?: number;
  employeeContracts?: TEmployeeContract[];
};

export type TDigitalSignature = {
  id?: string;
  name?: string;
  signatureType?: DigitalSignatureProcessSignatureType;
  //isGuestBiometric?: boolean;
  status?: DigitalSignatureStatus;
  category?: DigitalSignatureCategory;
  documents?: TDigitalSignatureDocument[];
  auditTrail?: TDigitalSignatureDocument;
  signedDocs?: TDigitalSignatureDocument;
  subscribers?: TSignatureSubscriber[];
  creator?: TDigitalSignatureCreator;
  startDate?: string;
  lastUpdateDate?: string;
  createdAt?: number;
  updateAt?: number;
  cancelledNotes?: string;
  rejectNotes?: string;
  failed?: any;
  totalSignatures?: number;
  employeeContract?: TEmployeeContract;
  customerContract?: TCustomerContracts;
  customer?: string;
  to?: string;
  customerName?: string;
  expirationTime?: string;
  isManual?: boolean;
  employeeStatus?: TEmployeeStatus; // CTT info if the employee is anonymized
  anonymized?: boolean; // CUTT info if the employee is anonymized
};

export type TDigitalSignatureCurrentUrl = {
  category?: TSignatureSubscriber;
  dsPadUrl?: string;
  email?: string;
  id?: string;
  name?: string;
  signUrl?: string;
};

export const digitalSignaturesApi = createApi({
  reducerPath: 'digitalSignatures',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_DIGITAL_SIGNATURES_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['DigitalSignature', 'DigitalSignatureURL'],
  endpoints: (builder) => ({
    getDigitalSignatures: builder.query<
      Response<TDigitalSignature>,
      TQueryParams
    >({
      providesTags: ['DigitalSignature'],
      query: formatQueryParams,
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TDigitalSignature>(
          res as JsonApiResponseDto<TDigitalSignature>,
          meta,
          args,
          true,
        );
      },
    }),

    getSingleDigitalSignature: builder.query<
      Response<TDigitalSignature>,
      { digitalSignatureId: string }
    >({
      providesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId }) => {
        return formatQueryForJsonApi({}, `${digitalSignatureId}`);
      },
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    getDigitalSignatureCurrentUrl: builder.query<
      Response<TDigitalSignatureCurrentUrl>,
      { digitalSignatureId: string }
    >({
      providesTags: ['DigitalSignatureURL'],
      query: ({ digitalSignatureId }) => {
        return formatQueryForJsonApi(
          {},
          `${digitalSignatureId}/currentSignUrl`,
        );
      },
      transformResponse: parseJsonApiResponse<TDigitalSignatureCurrentUrl>,
    }),

    getDocument: builder.mutation({
      queryFn: async (
        { digitalSignatureId, documentId },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `${digitalSignatureId}/document/${documentId}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);
          //const type = result.data.type.split('/')[1];
          hiddenElement.href = blobPDF;
          hiddenElement.target = '_blank';
          //hiddenElement.download = `${documentId}.${type}`;
          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),

    getRegisteredUsers: builder.query<
      Response<TRegisteredUsers>,
      { company: string }
    >({
      providesTags: ['DigitalSignature'],
      query: ({ company, ...params }) => {
        return formatQueryParams(params, `registered/${company}`);
      },
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TRegisteredUsers>(
          res as JsonApiResponseDto<TRegisteredUsers>,
          meta,
          args,
          true,
        );
      },
    }),
    //   POST

    // Notify digital signature workflow
    notifyDigitalSignature: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId }) =>
        formatRequest(
          { type: 'digital-signature' },
          Request.POST,
          `${digitalSignatureId}/workflow/notify`,
        ),
      // transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    // Notify digital signature workflow
    notifyBatchDigitalSignature: builder.mutation<
      { successes: number; failures: number },
      { ids: string[] }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ ids }) =>
        formatRequest(
          { type: 'notify-subscribers-batch', ids },
          Request.POST,
          `workflow/notify`,
        ),
      // transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    // Initiate digital signature process with DigitalSign
    initWorkflowDigitalSignature: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId }) =>
        formatRequest(
          { type: 'digital-signature' },
          Request.POST,
          `${digitalSignatureId}/workflow`,
        ),
      // transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    // Initiate digital signature process with DigitalSign
    initBatchWorkflowDigitalSignature: builder.mutation<
      { successes: number; failures: number },
      { ids: string[] }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ ids }) =>
        formatRequest(
          {
            type: 'start-workflow-batch',
            ids,
          },
          Request.POST,
          `workflow`,
        ),
    }),

    // Cancel digital signature process with DigitalSign
    cancelWorkflowDigitalSignature: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string; cancelledNotes: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, cancelledNotes }) =>
        formatRequest(
          { type: 'digital-signature', cancelledNotes },
          Request.POST,
          `${digitalSignatureId}/workflow/cancel`,
        ),
      // transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    batchCancelWorkflowDigitalSignature: builder.mutation<
      { successes: number; failures: number },
      { ids: string[]; cancelledNotes: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ ids, cancelledNotes }) =>
        formatRequest(
          { type: 'cancel-subscribers-batch', ids, cancelledNotes },
          Request.POST,
          `workflow/cancel`,
        ),
      // transformResponse: parseJsonApiResponse
    }),

    nulledDigitalSignatureProcess: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId }) =>
        formatRequest(
          { type: 'digital-signature' },
          Request.POST,
          `${digitalSignatureId}/nulled`,
        ),
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    changeDigitalSignatureProcessStatus: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string; status: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, status, ...params }) =>
        formatRequest(
          { type: 'change-process-status', status },
          Request.POST,
          `${digitalSignatureId}/status`,
        ),
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    syncWithDigitalSign: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, ...params }) =>
        formatRequest(
          { type: 'digital-signature' },
          Request.POST,
          `${digitalSignatureId}/sync`,
        ),
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    // Create digital signature process
    postCreateProcess: builder.mutation<
      Response<TDigitalSignature>,
      Omit<TDigitalSignature, 'id' | 'status' | 'documents' | 'subscribers'>
    >({
      invalidatesTags: ['DigitalSignature'],
      query: (params) =>
        formatRequest(
          { ...params, type: 'digital-signature' },
          Request.POST,
          '',
          ['employeeContract', 'cutt', 'cutt.responsibleId'],
        ),
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    // PATCH
    uploadFile: builder.mutation({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, file }) => {
        const body = new FormData();
        body.append('Content-Type', file.type);
        body.append('document', file);

        return {
          url: `${digitalSignatureId}/document`,
          method: Request.PATCH,
          body,
        };
      },
    }),

    addSubscribers: builder.mutation<
      Response<TSignatureSubscriber>,
      { digitalSignatureId: string; subscribers: TSignatureSubscriber[] }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, subscribers }) => {
        return {
          url: `${digitalSignatureId}/subscriber`,
          body: {
            data: {
              type: 'digital-signature-process-subscriber',
              attributes: {
                subscribers,
              },
            },
          },
          method: Request.PATCH,
        };
      },
      transformResponse: parseJsonApiResponse<TSignatureSubscriber>,
    }),

    editSubscriber: builder.mutation<
      Response<TSignatureSubscriber>,
      {
        digitalSignatureId: string;
        subscriberId: string;
        email?: string;
        phone?: string;
        shouldRead?: boolean;
        shouldReceiveSignedDocs?: boolean;
        notify?: boolean;
        signatureType?: DigitalSignatureSubscriberType;
      }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({
        digitalSignatureId,
        subscriberId,
        email,
        phone,
        shouldRead,
        shouldReceiveSignedDocs,
        notify,
        signatureType,
      }) => {
        return {
          url: `${digitalSignatureId}/subscriber/${subscriberId}`,
          body: {
            data: {
              type: 'digital-signature-process-subscriber',
              attributes: {
                email: email,
                phone: phone,
                shouldRead: shouldRead,
                shouldReceiveSignedDocs: shouldReceiveSignedDocs,
                notify: notify,
                category: signatureType,
              },
            },
          },
          method: Request.PATCH,
        };
      },
      transformResponse: parseJsonApiResponse<TSignatureSubscriber>,
    }),
    //DELETE
    deleteProcess: builder.mutation<
      Response<TDigitalSignature>,
      { id: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ id, ...params }) =>
        formatRequest(
          { ...params, type: 'digital-signature' },
          Request.DELETE,
          id,
        ),
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),

    batchDeleteWorkflowDigitalSignature: builder.mutation<
      { successes: number; failures: number },
      { ids: string[] }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ ids }) =>
        formatRequest(
          { type: 'delete-subscribers-batch', ids },
          Request.POST,
          `workflow/delete`,
        ),
      // transformResponse: parseJsonApiResponse
    }),

    deleteSubscriber: builder.mutation<
      Response<TDigitalSignature>,
      { digitalSignatureId: string; subscriberId: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, subscriberId, ...params }) =>
        formatRequest(
          { ...params, type: 'digital-signature-process-subscriber' },
          Request.DELETE,
          `${digitalSignatureId}/subscriber/${subscriberId}`,
        ),
      transformResponse: parseJsonApiResponse<TDigitalSignature>,
    }),
    deleteDocument: builder.mutation<
      Response<any>,
      { digitalSignatureId: string; documentId: string }
    >({
      invalidatesTags: ['DigitalSignature'],
      query: ({ digitalSignatureId, documentId, ...params }) =>
        formatRequest(
          { type: 'digital-signature' },
          Request.DELETE,
          `${digitalSignatureId}/document/${documentId}`,
        ),
    }),
  }),
});

export const {
  useGetDigitalSignaturesQuery,
  useGetSingleDigitalSignatureQuery,
  useGetDocumentMutation,
  useLazyGetDigitalSignatureCurrentUrlQuery,
  useInitWorkflowDigitalSignatureMutation,
  useNotifyDigitalSignatureMutation,
  useCancelWorkflowDigitalSignatureMutation,
  useBatchCancelWorkflowDigitalSignatureMutation,
  useNulledDigitalSignatureProcessMutation,
  usePostCreateProcessMutation,
  useDeleteProcessMutation,
  useAddSubscribersMutation,
  useEditSubscriberMutation,
  useGetRegisteredUsersQuery,
  useDeleteSubscriberMutation,
  useUploadFileMutation,
  useDeleteDocumentMutation,
  useBatchDeleteWorkflowDigitalSignatureMutation,
  useInitBatchWorkflowDigitalSignatureMutation,
  useNotifyBatchDigitalSignatureMutation,
  useChangeDigitalSignatureProcessStatusMutation,
  useSyncWithDigitalSignMutation,
} = digitalSignaturesApi;
